// Data migration utilities for safe encryption rollout
import { getAuth } from 'firebase/auth';
import { encryptData, decryptData } from './encryption.js';

// Check if user has existing data that needs migration
export const checkDataMigrationNeeded = async (db) => {
  try {
    const projects = await db.projects.toArray();
    const needsMigration = projects.some(project => 
      !project.encryptedData && (project.customerAddress || project.customerPhone)
    );
    
    return {
      needsMigration,
      projectCount: projects.length,
      unencryptedCount: projects.filter(p => !p.encryptedData).length
    };
  } catch (error) {
    console.error('Error checking migration status:', error);
    return { needsMigration: false, projectCount: 0, unencryptedCount: 0 };
  }
};

// Safely migrate existing data to encrypted format
export const migrateDataToEncrypted = async (db) => {
  const auth = getAuth();
  const user = auth.currentUser;
  
  if (!user) {
    throw new Error('User must be authenticated to migrate data');
  }
  
  try {
    const projects = await db.projects.toArray();
    let migratedCount = 0;
    let errorCount = 0;
    
    for (const project of projects) {
      // Skip if already encrypted
      if (project.encryptedData) continue;
      
      // Skip if no sensitive data
      if (!project.customerAddress && !project.customerPhone) continue;
      
      try {
        // Get equipment and conductors from separate tables
        const equipment = await db.equipment.where('projectId').equals(project.id).toArray();
        const conductors = await db.conductors.where('projectId').equals(project.id).toArray();
        
        // Prepare sensitive data for encryption
        const sensitiveData = {
          customerAddress: project.customerAddress || '',
          customerPhone: project.customerPhone || '',
          equipment: equipment || [],
          conductors: conductors || []
        };
        
        // Encrypt the data
        const encryptedData = await encryptData(sensitiveData);
        
        // Update project with encrypted data
        await db.projects.update(project.id, {
          encryptedData,
          customerAddress: null, // Remove unencrypted data
          customerPhone: null    // Remove unencrypted data
        });
        
        migratedCount++;
        console.log(`Migrated project: ${project.name}`);
        
      } catch (error) {
        console.error(`Failed to migrate project ${project.name}:`, error);
        errorCount++;
      }
    }
    
    return {
      success: true,
      migratedCount,
      errorCount,
      totalProjects: projects.length
    };
    
  } catch (error) {
    console.error('Migration failed:', error);
    return {
      success: false,
      error: error.message,
      migratedCount: 0,
      errorCount: 0
    };
  }
};

// Test encryption/decryption without affecting data
export const testEncryption = async () => {
  try {
    const testData = {
      customerAddress: '123 Test St',
      customerPhone: '555-1234',
      equipment: [{ type: 'pole', lat: 40.7128, lng: -74.0060 }],
      conductors: [{ type: 'overhead', coordinates: [{ lat: 40.7128, lng: -74.0060 }] }]
    };
    
    const encrypted = await encryptData(testData);
    const decrypted = await decryptData(encrypted);
    
    const success = JSON.stringify(testData) === JSON.stringify(decrypted);
    
    return {
      success,
      testData,
      encrypted: encrypted.substring(0, 50) + '...',
      decrypted
    };
  } catch (error) {
    return {
      success: false,
      error: error.message
    };
  }
};

// Backup data before migration
export const backupData = async (db) => {
  try {
    const projects = await db.projects.toArray();
    const equipment = await db.equipment.toArray();
    const conductors = await db.conductors.toArray();
    
    const backup = {
      timestamp: new Date().toISOString(),
      projects,
      equipment,
      conductors
    };
    
    // Store backup in localStorage
    localStorage.setItem('gridmap_backup_' + Date.now(), JSON.stringify(backup));
    
    return {
      success: true,
      projectCount: projects.length,
      equipmentCount: equipment.length,
      conductorCount: conductors.length
    };
  } catch (error) {
    return {
      success: false,
      error: error.message
    };
  }
}; 